import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

const statusOptions = [
  { value: "upcoming", label: "Upcoming" },
  { value: "active", label: "Active" },
  { value: "locked", label: "Locked" },
  { value: "completed", label: "Completed" },
];

const EditMatchdayModal = ({ open, onClose, matchday, onSubmit }) => {
  const [formData, setFormData] = useState({
    matchdayNumber: "",
    name: "",
    expectedMatchCount: 4,
    status: "upcoming",
    budgetInitial: "",
    startsAt: null,
    endsAt: null,
    locksAt: null,
  });

  const [errors, setErrors] = useState({});

  // Load matchday data when modal opens
  useEffect(() => {
    if (open && matchday) {
      setFormData({
        matchdayNumber: matchday.matchdayNumber || "",
        name: matchday.name || "",
        expectedMatchCount: matchday.expectedMatchCount || 4,
        status: matchday.status || "upcoming",
        budgetInitial: matchday.budgetInitial ?? "",
        startsAt: matchday.startsAt ? new Date(matchday.startsAt) : null,
        endsAt: matchday.endsAt ? new Date(matchday.endsAt) : null,
        locksAt: matchday.locksAt ? new Date(matchday.locksAt) : null,
      });
      setErrors({});
    }
  }, [open, matchday]);

  const validate = () => {
    const newErrors = {};

    if (!formData.matchdayNumber || formData.matchdayNumber < 1) {
      newErrors.matchdayNumber =
        "Matchday number is required and must be at least 1";
    }

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.expectedMatchCount || formData.expectedMatchCount < 1) {
      newErrors.expectedMatchCount = "Expected match count must be at least 1";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSubmit({
        ...formData,
        matchdayNumber: parseInt(formData.matchdayNumber),
        expectedMatchCount: parseInt(formData.expectedMatchCount),
        budgetInitial: formData.budgetInitial
          ? parseInt(formData.budgetInitial)
          : undefined,
      });
      onClose();
    }
  };

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  if (!matchday) return null;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Edit Matchday</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Matchday Number *"
                  type="number"
                  value={formData.matchdayNumber}
                  onChange={handleChange("matchdayNumber")}
                  error={!!errors.matchdayNumber}
                  helperText={errors.matchdayNumber}
                  inputProps={{ min: 1 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Expected Match Count *"
                  type="number"
                  value={formData.expectedMatchCount}
                  onChange={handleChange("expectedMatchCount")}
                  error={!!errors.expectedMatchCount}
                  helperText={errors.expectedMatchCount}
                  inputProps={{ min: 1 }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Matchday Name *"
                  value={formData.name}
                  onChange={handleChange("name")}
                  error={!!errors.name}
                  helperText={errors.name}
                  placeholder="Enter matchday name"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.status}>
                  <InputLabel>Status *</InputLabel>
                  <Select
                    value={formData.status}
                    onChange={handleChange("status")}
                    label="Status *"
                  >
                    {statusOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Initial Budget"
                  type="number"
                  value={formData.budgetInitial}
                  onChange={handleChange("budgetInitial")}
                  helperText="Team budget for this matchday (e.g. 50000000)"
                  inputProps={{ min: 0 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <DateTimePicker
                  label="Starts At"
                  value={formData.startsAt}
                  onChange={() => {}}
                  disabled
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      disabled
                      helperText={
                        formData.startsAt
                          ? "Auto-calculated from earliest match"
                          : "Will be calculated when matches are assigned"
                      }
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <DateTimePicker
                  label="Ends At"
                  value={formData.endsAt}
                  onChange={() => {}}
                  disabled
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      disabled
                      helperText={
                        formData.endsAt
                          ? "Auto-calculated from latest match"
                          : "Will be calculated when matches are assigned"
                      }
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <DateTimePicker
                  label="Locks At (Transfer Deadline)"
                  value={formData.locksAt}
                  onChange={() => {}}
                  disabled
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      disabled
                      helperText={
                        formData.locksAt
                          ? "90 minutes before first match"
                          : "Will be calculated when matches are assigned"
                      }
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Alert severity="info">
                  <strong>Auto-Calculated Fields:</strong> Start time, end time,
                  and lock time are automatically calculated based on the
                  matches assigned to this matchday and cannot be manually
                  edited.
                </Alert>
              </Grid>

              <Grid item xs={12}>
                <Alert severity="info">
                  <strong>Status Guide:</strong>
                  <br />• <strong>Upcoming:</strong> Matchday is scheduled but
                  not yet active
                  <br />• <strong>Active:</strong> Users can make transfers and
                  changes
                  <br />• <strong>Locked:</strong> Transfers are closed, matches
                  are live or about to start
                  <br />• <strong>Completed:</strong> All matches finished,
                  points calculated
                </Alert>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="contained">
              Save Changes
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </LocalizationProvider>
  );
};

export default EditMatchdayModal;
